import { FieldPath } from '@form-ts/core';
import { useFormField, useFormWatch } from '@form-ts/react';
import { pipe } from 'fp-ts/function';
import React, { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import { TextArea, TextAreaProps } from 'src/modules/common/components/TextArea';
import { FormError } from 'src/modules/form/types/FormError';

type Props<TData> = TextAreaProps & {
  readonly field: FieldPath<TData, FormError, string>;
  readonly onBlur?: () => void;
  readonly renderError?: (error: FormError) => React.ReactNode;
};

export const TextAreaField: <TData>(props: Props<TData>) => React.ReactElement = (
  {
    field,
    onBlur,
    renderError = renderErrorDefault,
    ...props
  },
) => {
  const { meta, value, error } = useFormField(field);

  const submitted = useFormWatch(field.form, field.form.submitted.get);
  const invalid = error !== undefined && (meta.touched || submitted);

  const handleChange = useCallback((event: React.ChangeEvent<HTMLTextAreaElement>) => {
    field.form.change(pipe(
      field.form.currentState,
      field.value.set(event.target.value),
    ));
  }, [field]);

  const handleBlur = useCallback(() => {
    field.form.change(pipe(
      field.form.currentState,
      field.touched.set(true),
    ));

    onBlur?.();
  }, [field, onBlur]);

  return (
    <TextArea
      {...props}
      id={`${field.form.name}.${field.path}`}
      name={field.path}
      invalid={invalid}
      invalidText={error && invalid ? renderError(error) : null}
      description={props.description === undefined ? (
        <FormattedMessage
          id="form/helper/maxLength"
          values={{ actualLength: value.length, maxLength: props.maxLength }}
        />
      ) : props.description}
      value={value}
      onChange={handleChange}
      onBlur={handleBlur}
    />
  );
};

const renderErrorDefault = (error: FormError): React.ReactNode => (
  <FormattedMessage id={`form/error/${error.code}`} values={error.context}/>
);
